import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/Card';
import { SifPreflightView, sifCheckFixPath } from '../../../../models/payroll';

/** Company setup pre-validation, with a Fix link on each failed check. Shared by the UAE and KSA screens. */
export const CompanySetupCard: React.FC<{ preflight: SifPreflightView }> = ({ preflight }) => {
  const { t } = useTranslation();
  if (preflight.companyChecks.length === 0) return null;
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold">
            {t('payroll.complianceCenter.wps.companySetupHeader', 'Company Setup Pre-Validation')}
          </h3>
          {preflight.companyPassed ? (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              {t('payroll.complianceCenter.wps.allPassed', 'All passed')}
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200 inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {t('payroll.complianceCenter.wps.needsAttention', 'Needs attention')}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          {t('payroll.complianceCenter.wps.companySetupSubtitle', 'Configuration the SIF needs at the company level. Each failed check links to where you can fix it.')}
        </p>
        <ul className="divide-y divide-border">
          {preflight.companyChecks.map((check, i) => {
            const fixTo = sifCheckFixPath(check.code);
            return (
              <li key={check.code || i} className="py-3">
                <div className="flex items-start gap-3">
                  {check.passed ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className={`text-sm font-medium ${check.passed ? '' : 'text-red-700'}`}>
                        {t(check.label, check.label)}
                      </p>
                      {check.detail && (
                        <span className="text-xs font-mono text-muted-foreground">
                          {/* The real value, such as a MOL number, shows as is. These two are the server's words for no value. */}
                          {check.detail === 'Missing' || check.detail === 'Not set' ? t(check.detail) : check.detail}
                        </span>
                      )}
                    </div>
                    {!check.passed && check.hint && (
                      <p className="text-xs text-muted-foreground mt-1">{t(check.hint, check.hint)}</p>
                    )}
                    {!check.passed && fixTo && (
                      <Link to={fixTo} className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                        {t('payroll.complianceCenter.wps.fixThis', 'Fix this')}
                        <ArrowRight className="w-3 h-3 rtl:rotate-180" aria-hidden="true" />
                      </Link>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};
